"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type UnsavedChangesContextValue = {
    hasUnsavedChanges: boolean;
    registerForm: (formId: string) => void;
    unregisterForm: (formId: string) => void;
    clearAll: () => void;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
    const [formIds, setFormIds] = useState<string[]>([]);

    const registerForm = useCallback((formId: string) => {
        setFormIds((current) => (current.includes(formId) ? current : [...current, formId]));
    }, []);

    const unregisterForm = useCallback((formId: string) => {
        setFormIds((current) => {
            if (!current.includes(formId)) {
                return current;
            }

            return current.filter((currentId) => currentId !== formId);
        });
    }, []);

    const clearAll = useCallback(() => setFormIds([]), []);

    const value = useMemo(
        () => ({
            hasUnsavedChanges: formIds.length > 0,
            registerForm,
            unregisterForm,
            clearAll,
        }),
        [formIds]
    );

    return (
        <UnsavedChangesContext.Provider value={value}>
            {children}
        </UnsavedChangesContext.Provider>
    );
}

export function useUnsavedChangesState() {
    const context = useContext(UnsavedChangesContext);

    if (!context) {
        throw new Error("useUnsavedChangesState must be used within UnsavedChangesProvider");
    }

    return context;
}

export function useUnsavedChangesFlag(isActive: boolean, formId: string) {
    const { registerForm, unregisterForm } = useUnsavedChangesState();
    const isActiveRef = useRef(isActive);

    useEffect(() => {
        isActiveRef.current = isActive;

        if (isActive) {
            registerForm(formId);
        }

        if (!isActive) {
            unregisterForm(formId);
        }
    }, [formId, isActive, registerForm, unregisterForm]);

    useEffect(() => {
        return () => {
            if (isActiveRef.current) {
                unregisterForm(formId);
            }
        };
    }, [formId, unregisterForm]);
}